import type { DropResponseDto } from '~/lib/api';

// upcoming -> active -> ending-soon -> processing -> completed
export type DropState =
  // drop is active
  | { type: 'active'; endTime: string }

  // drop is has been allocated - see if you won
  | { type: 'completed'; endTime: string }

  // drop will end soon
  | { type: 'ending-soon'; endTime: string }

  // drop is being allocated
  | { type: 'processing'; endTime: string }

  // drop has not started yet
  | { type: 'upcoming'; startTime: string };

const ENDING_SOON_THRESHOLD = 30000; // 30 seconds

export const isDropEndedAndAllocated = (drop: { status: string }): boolean => drop.status === 'completed';

export const isDropInFuture = (drop: { startTime: string }): boolean => new Date(drop.startTime) > new Date();

export const isDropEndingSoon = (drop: { endTime: string; status: string }): boolean => {
  if (drop.status === 'completed' || drop.status === 'ended') return false;
  const now = new Date().getTime();
  const endTime = new Date(drop.endTime).getTime();
  const timeLeft = endTime - now;
  return timeLeft > 0 && timeLeft <= ENDING_SOON_THRESHOLD;
};

export const isDropProcessing = (drop: { endTime: string; status: string }): boolean => {
  const hasEnded = new Date(drop.endTime) <= new Date();
  return hasEnded && drop.status !== 'completed';
};

export const isDropReadyForClaim = (drop: { status: string }): boolean => drop.status === 'completed';

export const getDropState = (drop: DropResponseDto): DropState => {
  if (isDropInFuture(drop)) {
    return { type: 'upcoming', startTime: drop.startTime };
  }

  if (isDropEndedAndAllocated(drop)) {
    const allowedToClaim = drop.claimAllowed;
    return { type: allowedToClaim ? 'completed' : 'processing', endTime: drop.endTime };
  }

  if (isDropProcessing(drop)) {
    return { type: 'processing', endTime: drop.endTime };
  }

  if (isDropEndingSoon(drop)) {
    return { type: 'ending-soon', endTime: drop.endTime };
  }

  return { type: 'active', endTime: drop.endTime };
};

export const getTotalPackCount = (dropCollections: { totalSupply: string }[]): number =>
  dropCollections.reduce((total, collection) => total + Number(collection.totalSupply), 0);
